/* eslint-disable max-lines */
import {
  PropTypes,
  getPropsPerDatatestIdPropTypes,
  getPropsPerSlotPropTypes,
  globalAttributesPropTypes,
  xstyledPropTypes,
  type GlobalAttributesT,
  type ValidationMap,
  type XstyledProps,
} from '@elliemae/ds-props-helpers';
import type { TypescriptHelpersT } from '@elliemae/ds-typescript-helpers';
import type React from 'react';
import type { ControlledDateTimePickerSlotEngine } from './slot-arguments-polymorphic-typescript.js';
import {
  ControlledDateTimePickerDatatestid,
  DSControlledDateTimePickerName,
  DSControlledDateTimePickerSlotsFlatten,
} from './constants/index.js';

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = (): void => {};
const defaultReturnFalse = (): false => false;

export declare namespace DSControlledDateTimePickerT {
  export interface MetaMonthDay {
    day: Date;
    dayAsString: string;
    isPrevMonthDay: boolean;
    isNextMonthDay: boolean;
    isCurrMonthDay: boolean;
    isHidden: boolean;
    dayBtnRef: React.MutableRefObject<HTMLButtonElement>;
  }

  /**
   * Arguments passed to the function form of the `dsControlleddatetimepickerCalendarDay` slot.
   * The function receives these at render time via `getOwnerPropsArguments` — use them to
   * compute state-aware aria attributes (e.g. range-specific labels in ds-form-date-range-picker).
   */
  export type CalendarDaySlotArguments = {
    isSelected: boolean;
    isFocused: boolean;
    isDisabled: boolean;
    isOutOfRange: boolean;
    isStartRangeDay: boolean;
    isEndRangeDay: boolean;
    /** True for all days inside the range, including start and end. */
    isDayInRangeImproper: boolean;
    /** True only for days strictly between start and end (excludes start/end). */
    isDayInRange: boolean;
    metaDay: MetaMonthDay;
  };

  /**
   * Slot function signatures for a given type, with CalendarDaySlotArguments
   * applied as the typed override for dsControlleddatetimepickerCalendarDay.
   * Only slots that exist in the DOM for T are present — passing a slot prop
   * for a non-existent slot is a compile-time error.
   */
  export type Slots<T extends ControlledDateTimePickerSlotEngine.ControlTypes> =
    ControlledDateTimePickerSlotEngine.SlotFunctionArguments<
      T,
      {
        dsControlleddatetimepickerCalendarDay: (args: CalendarDaySlotArguments) => object;
      }
    >;

  /**
   * Prop-facing slot type for a given type: each slot accepts either a plain
   * object (static form) or the typed function (dynamic form). All slots optional.
   */
  export type PolymorphicSlots<T extends ControlledDateTimePickerSlotEngine.ControlTypes> =
    ControlledDateTimePickerSlotEngine.PolymorphicSlotFunctionArguments<
      T,
      {
        dsControlleddatetimepickerCalendarDay: (args: CalendarDaySlotArguments) => object;
      }
    >;

  type PropsT<Default, Required, Optional, SpecificHTMLElement> = Partial<Default> &
    Required &
    Optional &
    Omit<GlobalAttributesT<SpecificHTMLElement>, keyof Default | keyof Required | keyof Optional> &
    Omit<XstyledProps, keyof Default | keyof Required | keyof Optional>;

  type InternalPropsT<Default, Required, Optional, SpecificHTMLElement> = Default &
    Required &
    Optional &
    Omit<GlobalAttributesT<SpecificHTMLElement>, keyof Default | keyof Required | keyof Optional> &
    Omit<XstyledProps, keyof Default | keyof Required | keyof Optional>;
  type DataTestIdType = typeof ControlledDateTimePickerDatatestid;
  // typescript type that rapresents the union of all the values for ControlledDateTimePickerDatatestid
  export type FlatDataTestIdValues = {
    [K in keyof DataTestIdType]: DataTestIdType[K] extends Record<string, unknown>
      ? DataTestIdType[K][keyof DataTestIdType[K]] // this is only 1 level deep, but it's enough for our needs right now
      : // can be extended to more levels if needed as follows:
        // ? DataTestIdType[K][keyof DataTestIdType[K]] extends Record<string, unknown>
        //   ? DataTestIdType[K][keyof DataTestIdType[K]][keyof DataTestIdType[K][keyof DataTestIdType[K]]]
        //   : DataTestIdType[K][keyof DataTestIdType[K]]
        DataTestIdType[K];
  }[keyof DataTestIdType];

  export type OnInternalValuesChangeEvent =
    | React.ChangeEvent<HTMLInputElement>
    | React.KeyboardEvent<HTMLInputElement>
    | React.KeyboardEvent<HTMLButtonElement>
    | React.KeyboardEvent
    | React.MouseEvent<HTMLInputElement>
    | React.MouseEvent<HTMLButtonElement>
    | React.MouseEvent<HTMLDivElement>;

  export type ControlTypes =
    | 'full-date-time'
    | 'date-time-inputs'
    | 'date-time-picker'
    | 'date-time-picker-controller-only'
    | 'full-date'
    | 'date-inputs'
    | 'date-picker'
    | 'date-picker-controller-only'
    | 'full-time'
    | 'time-inputs'
    | 'time-picker'
    | 'time-picker-controller-only';
  export interface InternalInputsChangeMetaInfo {
    isAutomaticFillTrigger?: boolean;
    isDayPresentInMonth?: boolean;
    isOutOfRangeDay?: boolean;
    isDisabledDay?: boolean;
    isDisabledTime?: boolean;
    suggestedErrorMsg?: string;
    newDateTimeString?: string;
    newDateString?: string;
    newTimeString?: string;
  }

  export type OnDateChange = (val: string, metaInfo: InternalInputsChangeMetaInfo) => void;
  export type OnTimeChange = (val: string, metaInfo: InternalInputsChangeMetaInfo) => void;
  export type OnDateTimeChange = (val: string, metaInfo: InternalInputsChangeMetaInfo) => void;
  export type OnInternalInputsChange = (
    val: string,
    e: OnInternalValuesChangeEvent,
    metaInfo?: InternalInputsChangeMetaInfo,
  ) => void;
  export type AppOnInputChange = (
    val: string,
    e: OnInternalValuesChangeEvent,
    metaInfo: InternalInputsChangeMetaInfo,
  ) => void;

  /* ------------------------ ------------------ ------------------------*/
  /* ------------------------ DATE CONFIGURATION ------------------------*/
  /* ------------------------ ------------------ ------------------------*/
  export interface DefaultDateProps {
    onMonthChange: AppOnInputChange;
    onDayChange: AppOnInputChange;
    onYearChange: AppOnInputChange;
    onPickerOpen: TypescriptHelpersT.VoidGenericFunc;
    onPickerClose: TypescriptHelpersT.VoidGenericFunc;
    getIsDisabledDay: TypescriptHelpersT.BooleanGetter<string>;
    getIsOutOfRangeDay: TypescriptHelpersT.BooleanGetter<string>;
    getIsStartRangeDay: TypescriptHelpersT.BooleanGetter<string>;
    getIsDayInRange: TypescriptHelpersT.BooleanGetter<string>;
    getIsEndRangeDay: TypescriptHelpersT.BooleanGetter<string>;
    isClearable: boolean;
    disabled: boolean;
    readOnly: boolean;
    hasError: boolean;
    autoFocus: boolean;
    preventCloseOnSelection: boolean;
  }
  export interface OptionalDatePickerProps extends PolymorphicSlots<'full-date'> {
    applyAriaDisabled?: boolean;
    emptyPickerStartingMonth?: string;
    onCalendarOpenFocusedDay?: string;
    pickerFooterMsg?: string;
    innerRef?:
      | React.MutableRefObject<HTMLInputElement | HTMLButtonElement | null>
      | ((ref: HTMLInputElement | HTMLButtonElement | null) => void);
    lastElementRef?: React.MutableRefObject<HTMLElement | null>;
    tabIndex?: TypescriptHelpersT.WCAGTabIndex;
    fromDate?: string;
    toDate?: string;
  }
  export interface RequiredDatePickerProps {
    type: 'full-date' | 'date-inputs' | 'date-picker' | 'date-picker-controller-only';
    date: string;
    onDateChange: OnDateChange;
  }
  export type DatePickerProps = PropsT<
    Partial<DefaultDateProps>,
    RequiredDatePickerProps,
    OptionalDatePickerProps,
    HTMLDivElement
  >;

  export type InternalDatePickerProps = InternalPropsT<
    DefaultDateProps,
    RequiredDatePickerProps,
    OptionalDatePickerProps,
    HTMLDivElement
  >;

  /* ------------------------ ------------------ ------------------------*/
  /* ------------------------ TIME CONFIGURATION ------------------------*/
  /* ------------------------ ------------------ ------------------------*/

  export interface DefaultTimeProps {
    onHourChange: AppOnInputChange;
    onMinuteChange: AppOnInputChange;
    onMeridiemChange: AppOnInputChange;
    onPickerOpen: TypescriptHelpersT.VoidGenericFunc;
    onPickerClose: TypescriptHelpersT.VoidGenericFunc;
    getIsDisabledTime: TypescriptHelpersT.BooleanGetter<string>;
    minutesInterval: number;
    isClearable: boolean;
    disabled: boolean;
    readOnly: boolean;
    hasError: boolean;
    autoFocus: boolean;
    preventCloseOnSelection: boolean;
  }
  export interface OptionalTimePickerProps extends PolymorphicSlots<'full-time'> {
    applyAriaDisabled?: boolean;
    pickerFooterMsg?: string;
    onTimeWheelOpenStartingTime?: string;
    innerRef?:
      | React.MutableRefObject<HTMLInputElement | HTMLButtonElement | null>
      | ((ref: HTMLInputElement | HTMLButtonElement | null) => void);
    lastElementRef?: React.MutableRefObject<HTMLElement | null>;
    tabIndex?: TypescriptHelpersT.WCAGTabIndex;
  }
  export interface RequiredTimePickerProps {
    type: 'full-time' | 'time-inputs' | 'time-picker' | 'time-picker-controller-only';
    time: string;
    onTimeChange: OnTimeChange;
  }
  export type TimePickerProps = PropsT<
    Partial<DefaultTimeProps>,
    RequiredTimePickerProps,
    OptionalTimePickerProps,
    HTMLDivElement
  >;

  export type InternalTimePickerProps = InternalPropsT<
    DefaultTimeProps,
    RequiredTimePickerProps,
    OptionalTimePickerProps,
    HTMLDivElement
  >;

  /* -------------------- ---------------------- ------------------------*/
  /* -------------------- DATETIME CONFIGURATION ------------------------*/
  /* -------------------- ---------------------- ------------------------*/

  export type DefaultDateTimeProps = DefaultTimeProps &
    DefaultDateProps & {
      onDateChange: OnDateChange;
      onTimeChange: OnTimeChange;
    };
  export type OptionalDateTimeProps = OptionalTimePickerProps &
    OptionalDatePickerProps &
    PolymorphicSlots<'full-date-time'>;
  export interface RequiredDateTimeProps {
    type: 'full-date-time' | 'date-time-inputs' | 'date-time-picker' | 'date-time-picker-controller-only';
    dateTime: string;
    onDateTimeChange: OnDateTimeChange;
  }

  export type DateTimePickerProps = PropsT<
    Partial<DefaultDateTimeProps>,
    RequiredDateTimeProps,
    OptionalDateTimeProps,
    HTMLDivElement
  >;
  export type InternalDateTimePickerProps = InternalPropsT<
    DefaultDateTimeProps,
    RequiredDateTimeProps,
    OptionalDateTimeProps,
    HTMLDivElement
  >;

  /* ---------------------- ------------------- -------------------------*/
  /* ---------------------- FINAL CONFIGURATION -------------------------*/
  /* ---------------------- ------------------- -------------------------*/
  export type DefaultProps = DefaultDateTimeProps & { type: 'full-date-time' };

  export type Props = DateTimePickerProps | DatePickerProps | TimePickerProps;
  export type InternalProps = InternalDateTimePickerProps | InternalDatePickerProps | InternalTimePickerProps;
}

export const defaultProps: DSControlledDateTimePickerT.DefaultProps = {
  type: 'full-date-time',
  onDateChange: noop,
  onMonthChange: noop,
  onDayChange: noop,
  onYearChange: noop,
  onTimeChange: noop,
  onHourChange: noop,
  onMinuteChange: noop,
  onMeridiemChange: noop,
  onPickerOpen: noop,
  onPickerClose: noop,
  getIsDisabledDay: defaultReturnFalse,
  getIsOutOfRangeDay: defaultReturnFalse,
  getIsStartRangeDay: defaultReturnFalse,
  getIsDayInRange: defaultReturnFalse,
  getIsEndRangeDay: defaultReturnFalse,
  getIsDisabledTime: defaultReturnFalse,
  minutesInterval: 1,
  isClearable: false,
  disabled: false,
  readOnly: false,
  hasError: false,
  autoFocus: false,
  preventCloseOnSelection: false,
};

export const propTypes = {
  ...getPropsPerSlotPropTypes(DSControlledDateTimePickerName, DSControlledDateTimePickerSlotsFlatten),
  ...globalAttributesPropTypes,
  ...xstyledPropTypes,
  type: PropTypes.oneOf([
    'full-date-time',
    'date-time-inputs',
    'date-time-picker',
    'date-time-picker-controller-only',
    'full-date',
    'date-inputs',
    'date-picker',
    'date-picker-controller-only',
    'full-time',
    'time-inputs',
    'time-picker',
    'time-picker-controller-only',
  ])
    .description('Type of time form control to use')
    .defaultValue('full-date-time'),
  disabled: PropTypes.bool.description('wheter or not the input is disabled').defaultValue(false),
  applyAriaDisabled: PropTypes.bool.description(
    'Whether to apply disabled styling and announce as disabled in SR. CAN STILL RECEIVE FOCUS. PREVENTS ONLY MAIN ACTION.',
  ),
  readOnly: PropTypes.bool
    .description('Makes the component non-editable while remaining accessible')
    .defaultValue(false),
  isClearable: PropTypes.bool.description('with or without clearable button').defaultValue(false),
  dateTime: PropTypes.string.description('when using date-time types, this is the value of the controller'),
  date: PropTypes.string.description('when using date types, this is the value of the controller'),
  time: PropTypes.string.description('when using time types, this is the value of the controller'),
  onDateChange: PropTypes.func.description('(newDateString, metaInformations) => void'),
  onTimeChange: PropTypes.func.description('(newTimeString, metaInformations) => void'),
  onDateTimeChange: PropTypes.func.description('(newDateTimeString, metaInformations) => void'),
  onMonthChange: PropTypes.func.description('(newMonthString, event, metaInformations) => void'),
  onDayChange: PropTypes.func.description('(newDayString, event, metaInformations) => void'),
  onYearChange: PropTypes.func.description('(newYearString, event, metaInformations) => void'),
  onHourChange: PropTypes.func.description('(newHourString, event, metaInformations) => void'),
  onMinuteChange: PropTypes.func.description('(newMinuteString, event, metaInformations) => void'),
  onMeridiemChange: PropTypes.func.description('(newMeridiemString, event, metaInformations) => void'),
  getIsDisabledDay: PropTypes.func.description(
    '(dayAsString) => true for marking as disabled, false for marking as valid',
  ),
  getIsOutOfRangeDay: PropTypes.func.description(
    '(dayAsString) => true for marking as disabled, false for marking as valid',
  ),
  getIsDisabledTime: PropTypes.func.description(
    '(timeAsString) => true for marking as disabled, false for marking as valid',
  ),
  emptyPickerStartingMonth: PropTypes.string.description(
    'which month to start the calendar on open, when value is empty, string following the "MM/__/YYYY" pattern',
  ),
  onCalendarOpenFocusedDay: PropTypes.string
    .description('which day to focus on calendar open when value is empty, string following the "MM/DD/YYYY" pattern')
    .defaultValue('if value is empty current month, else current month'),
  onTimeWheelOpenStartingTime: PropTypes.string
    .description(
      'which day to start the timewheel on picker open when value is empty, string following the HH:MM AA" pattern',
    )
    .defaultValue('01:00 AM'),
  minutesInterval: PropTypes.number
    .description('increments/decrements of minutes in time related controllers interactions')
    .defaultValue('1'),
  'data-testid': PropTypes.string.description('Unique id for tests.').defaultValue(''),
  ...getPropsPerDatatestIdPropTypes(ControlledDateTimePickerDatatestid),
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).description('inner ref for the month input'),
  lastElementRef: PropTypes.oneOfType([PropTypes.object]).description(
    'ref for the last focusable element in the component based on its configuration, used for focus management in focus trap logic',
  ),
  fromDate: PropTypes.string.description(
    `Strictly formatted string representing user's selected "from" date, this prop comes from the DateRangePicker and it's used internally for the range selection announcement`,
  ),
  toDate: PropTypes.string.description(
    `Strictly formatted string representing user's selected "to" date, this prop comes from the DateRangePicker and it's used internally for the range selection announcement`,
  ),
  hasError: PropTypes.bool.description('whether the input has error or not').defaultValue(false),
} as unknown as ValidationMap<DSControlledDateTimePickerT.Props>;
